<template>
  <div
    class="flex border-b border-[color:var(--color-header-footer-border)] bg-[color:var(--color-header-top-bg)] text-sm text-[color:var(--color-header-top-text)]"
  >
    <div class="container mx-auto flex h-[50px] max-w-screen-xl items-center justify-between">
      <div class="flex items-center">
        <div class="mr-2.5 flex">
          <div class="mr-2.5 flex items-center">
            <VcIcon name="checkmark" class="mt-1 text-primary-500" :size="18" lazy />

            <p class="text-13">{{ $t("custom.topbar.usps.quick_delivery") }}</p>
          </div>
          <div class="mr-2.5 flex items-center">
            <VcIcon name="checkmark" class="mt-1 text-primary-500" :size="18" lazy />

            <a v-if="vcaUrl" class="text-13 underline" :href="vcaUrl" target="_blank">{{
              $t("custom.topbar.usps.partner_in_vca")
            }}</a>
            <p v-else class="text-13">{{ $t("custom.topbar.usps.reliable_partner") }}</p>
          </div>
          <div class="mr-2.5 flex items-center">
            <VcIcon name="checkmark" class="mt-1 text-primary-500" :size="18" lazy />

            <p class="text-13">{{ $t("custom.topbar.usps.wild_range") }}</p>
          </div>

          <div class="mr-2 flex xl:mr-4">
            <SocialLinks />
          </div>
        </div>
      </div>

      <div class="flex items-center text-13">
        <div class="flex items-center text-base">
          <a
            class="inline-flex items-center hover:text-[color:var(--color-primary-500)]"
            :href="phoneNumber.url"
            :target="phoneNumber.newWindow ? '_blank' : ''"
          >
            <VcIcon class="me-1.5 fill-primary" name="phone-contact" size="sm" /> {{ phoneNumber.label }}
          </a>
        </div>

        <LanguageSelector class="ml-4" v-if="$context.availableLanguages && $context.availableLanguages.length > 1" />
        <!-- <CurrencySelector v-if="$context.availCurrencies && $context.availCurrencies.length > 1" /> -->

        <!-- Authorized menu items -->
        <div v-if="isAuthenticated" class="ml-3">
          <div ref="loginMenu" class="relative flex flex-row items-center gap-x-1">
            <template v-if="operator">
              <span class="font-bold">{{ operator.contact?.fullName || operator.userName }}</span>
              <span v-t="'shared.layout.header.top_header.logged_in_as'" class="text-white" />
            </template>

            <button
              type="button"
              class="flex cursor-pointer items-center py-1 text-white hover:text-[color:var(--color-header-top-link-hover)]"
              @click="loginMenuVisible = !loginMenuVisible"
            >
              <span class="font-bold">{{ user.contact?.fullName || user.userName }}</span>

              <VcIcon
                :size="10"
                class="ml-2 text-[--color-primary] lg:text-[--color-primary]"
                :name="loginMenuVisible ? 'chevron-up' : 'chevron-down'"
              />
            </button>

            <div
              v-if="loginMenuVisible"
              class="absolute right-0 top-full z-10 flex w-60 flex-col space-y-3 rounded-md bg-white px-3 py-4 text-black shadow-md"
            >
              <div class="flex items-center justify-between">
                <router-link
                  to="/account/dashboard"
                  class="flex items-center hover:text-[color:var(--color-primary-500)]"
                  @click="loginMenuVisible = false"
                >
                  <VcIcon class="text-[--color-primary]" name="user-circle" />

                  <span class="ml-2">{{ user.contact?.fullName }}</span>
                </router-link>

                <div class="grow"></div>

                <VcButton
                  :title="$t('shared.layout.header.link_logout')"
                  class="ml-4"
                  variant="outline"
                  color="neutral"
                  size="xs"
                  icon
                  @click="signOut"
                >
                  <VcIcon name="logout" />
                </VcButton>
              </div>
              <div v-if="isAdministrator" class="flex w-full" @click="loginMenuVisible = false">
                <router-link
                  :to="{ name: 'ImpersonateUserDashboard' }"
                  class="flex w-full items-center justify-between hover:text-[color:var(--color-primary-500)]"
                >
                  <span class="ml-2">{{ $t("shared.account.sign_in_form.impersonate.switch_user") }}</span>
                  <VcButton
                    class="px-2"
                    variant="outline"
                    color="neutral"
                    size="xs"
                    :title="$t('shared.layout.header.link_logout')"
                  >
                    <VcIcon name="rotate" />
                  </VcButton>
                </router-link>
              </div>
            </div>

            <div v-if="isMultiOrganization" class="border-t py-3">
              <div class="px-3 py-1 text-xs text-neutral-600">
                {{ $t("common.labels.organizations") }}
              </div>

              <VcRadioButton
                v-for="item in user.contact?.organizations?.items"
                :key="item.id"
                v-model="contactOrganizationId"
                :label="item.name"
                :value="item.id"
                class="flex px-3 py-1 text-sm"
                @change="selectOrganization"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { ref, computed } from "vue";
import { useSignMeOut, useUser } from "@/shared/account";
import { SocialLinks } from "@/shared/layout";
import LanguageSelector from "@/shared/layout/components/language-selector/language-selector.vue";
import { globals } from "@/core/globals";

const { isAuthenticated, isMultiOrganization, user, operator, isAdministrator, switchOrganization } = useUser();

const { storeId } = globals;

const { signMeOut } = useSignMeOut();

const loginMenu = ref(null);
const loginMenuVisible = ref(false);
const contactOrganizationId = ref(user.value?.contact?.organizationId);

async function selectOrganization(): Promise<void> {
  if (!contactOrganizationId.value) {
    return;
  }

  await switchOrganization(contactOrganizationId.value);
  loginMenuVisible.value = false;
}

async function signOut() {
  await signMeOut();
  location.href = "/";
}

onClickOutside(loginMenu, () => {
  loginMenuVisible.value = false;
});

type PhoneNumberType = { label: string; url: string; newWindow?: string | null };

const phoneNumber = computed<PhoneNumberType>(() =>
  storeId === "HerasBE"
    ? {
        label: "+32 (0)13 677 834",
        url: "tel:+32 (0)13 677 834",
        icon: "/static/icons/social/phone-contact.svg",
        newWindow: null,
      }
    : storeId === "HerasFR"
      ? { label: "+33 (0)474 36 67 11", url: "tel:+33 (0)474 36 67 11", newWindow: null }
      : { label: "+31 (0)499 551 551", url: "tel:+31 (0)499 551 551", newWindow: null },
);

const vcaUrl = computed<string | null>(() => {
  if (storeId === "HerasBE") return "https://herasmobilevirto.blob.core.windows.net/vca/VCA-certificaat-BE.pdf";
  if (storeId === "HerasNL") return "https://herasmobilevirto.blob.core.windows.net/vca/VCA-certificaat-NL.pdf";
  return null;
});
</script>
